import { useCallback, useEffect, useRef } from 'react';
import type { ChatMessage } from '../../types/agent';
import { isCardVisible } from '../../services/agent/ChatSession';
import { MessageBubble, type BubbleActions } from './MessageBubble';
import { useDeparting } from './motion';

const keyOf = (m: ChatMessage) => m.id;
/** A turn with nothing left to show (e.g. reloaded history made only of finished steps) is hidden; a card (an
 *  audit, a plan) is something to show. */
const isTurnVisible = (m: ChatMessage) => m.role === 'user' || !!m.content.trim() || !!m.pending || !!m.error || !!m.audit || !!m.plan
  || !!m.toolCards?.some(isCardVisible);

/** Scrolling turn list (design 1b), auto-scrolled to the newest content. Turns that just lost their
 *  last visible step stay mounted for the leave transition (`useDeparting`); a ResizeObserver keeps
 *  the list pinned to the bottom while rows collapse or text streams, but only if the user was
 *  already at the bottom. */
export function MessageList({ messages, actions }: { messages: ChatMessage[]; actions: BubbleActions }) {
  const ref = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const isVisible = useCallback(isTurnVisible, []);
  const rendered = useDeparting(messages, keyOf, isVisible);

  // A new turn: the user's own message always brings the list back down (they just sent it); anything else
  // follows only if they were already at the bottom. Streamed text is NOT handled here — it would fire on
  // every token and fight the user's scrolling — the ResizeObserver below follows it instead.
  const last = messages[messages.length - 1];
  const lastKey = last ? `${last.id}:${last.role}` : '';
  useEffect(() => {
    const el = ref.current;
    if (!el || !last) return;
    if (last.role === 'user') atBottom.current = true;
    if (atBottom.current) requestAnimationFrame(() => el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }));
  }, [lastKey]);

  useEffect(() => {
    const el = ref.current;
    const content = inner.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return;
    // Following is the user's call. Moving up — wheel, trackpad, scrollbar, keys — stops it at once, even by
    // a pixel; coming back to the bottom resumes it. Growth alone never changes scrollTop, and our own
    // scrolls only ever move down, so neither can be mistaken for the user leaving.
    // A scroll up counts as the user's only while they are holding the scrollbar or pressing a key: content that
    // collapses (a finished batch, a settled card) moves scrollTop too, and taken for the user it stopped the list
    // following the reply that came right after a plan.
    let lastTop = el.scrollTop;
    let handling = false;
    const onScroll = () => {
      const top = el.scrollTop;
      if (el.scrollHeight - (top + el.clientHeight) <= 8) atBottom.current = true;
      else if (top < lastTop && handling) atBottom.current = false;
      lastTop = top;
    };
    const onPointerDown = () => {
      handling = true;
    };
    const onPointerUp = () => {
      handling = false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowUp', 'PageUp', 'Home'].includes(e.key)) atBottom.current = false;
    };
    // A wheel or touch up stops following before the next frame of streamed text can pull the list back.
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) atBottom.current = false;
    };
    const onTouch = () => {
      atBottom.current = false;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchmove', onTouch, { passive: true });
    el.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    el.addEventListener('keydown', onKey);
    const ro = new ResizeObserver(() => {
      if (atBottom.current) el.scrollTo({ top: el.scrollHeight });
    });
    ro.observe(content);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchmove', onTouch);
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('keydown', onKey);
      ro.disconnect();
    };
  }, []);

  return (
    // `overflow-anchor: none`: the list pins itself to the bottom. The browser's scroll anchoring moved scrollTop up
    // when steps above collapsed as the reply below grew, which read as the user scrolling away.
    <div ref={ref} className="flex flex-1 flex-col overflow-y-auto px-3.5 py-4 [overflow-anchor:none]">
      <div ref={inner} className="flex flex-col gap-3.5">
        {rendered.map(({ item: m }) => (
          <MessageBubble key={m.id} m={m} actions={actions} />
        ))}
      </div>
    </div>
  );
}
